/** INTERIORUL ETAJULUI TĂIAT, pe macheta de situație — geometria (v.
 *  `interiorEtaj.ts` pentru ce se știe despre etaj).
 *
 *  Ce se construiește, pentru toate corpurile etajului ales:
 *    * PEREȚII, de la pardoseală până la tăietură (sub placa de deasupra), în
 *      albul machetei; fața lor de sus, abia mai închisă, citește tăietura;
 *    * la fiecare gol, PARAPETUL de sub fereastră și BUIANDRUGUL de deasupra
 *      golului — sticla și tâmplăria le desenează deja fațada;
 *    * PLANUL ETAJULUI COPT într-o imagine pe pardoseală: nuanța palidă a
 *      tipului de apartament, mobilierul de planșă, ușile, lifturile și ghena,
 *      numele și aria camerelor;
 *    * SCĂRILE NUCLEULUI în 3D: treptele, câte contratrepte cere înălțimea
 *      etajului (aceeași regulă ca planul, `scara.ts`), până la tăietură.
 *
 *  DE CE E IEFTIN la desenat: toți pereții etajului sunt PATRU plase (capacele
 *  și laturile, pe perete de compartimentare și pe zid), iar pardoseala e câte
 *  o imagine pe corp — oricâte camere și piese de mobilier. Lipiți cu
 *  grupurile extrudării, pereții ar fi fost două desene fiecare, sute pe etaj. */
import * as THREE from "three";
import * as BGU from "three/examples/jsm/utils/BufferGeometryUtils.js";
import type React from "react";
import { renderToStaticMarkup } from "react-dom/server";
import type { StoreyPlan, StoreyShape } from "../canonical";
import type { Fatada3 } from "./sit3d";
import { cadruCamerei, piesaPlan, piesaSingura } from "./arhMobilierPlan";
import { scaraDin } from "./scara";
import { insetInel } from "./panou3d";
import { apartamenteleEtajului, distContur, faraVariabile, inPoligon, planulEtajului,
         type ApartamentEtaj } from "./interiorEtaj";
import { taie } from "../taietor";
import { FINISAJ_M, mainiCurente, platforma3d, treptePeAmprenta } from "./trepte3d";

/** Zidul de fațadă se retrage 3 mm spre interior: fața lui de afară ar fi
 *  căzut în planul din spate al pielii de fațadă (panourile pornesc din planul
 *  zidăriei) și cele două s-ar fi bătut prin goluri. */
const RETRAS_FATADA_M = 0.003;
/** Cota minimă a pardoselii coapte: terenul machetei își pune straturile până
 *  la 6 cm (iarba, aleile, locurile de parcare), iar pardoseala parterului,
 *  la cota 0, ar fi stat sub ele. */
const PESTE_TEREN_M = 0.08;
/** Pixeli pe metru în planul copt: liniile mobilierului (1–1,5 px) rămân
 *  linii, iar un corp de 75 m încape într-o textură de 3.600 px. */
const PX_PE_M = 48;

export interface OptiuniInterior {
  /** Numele camerei, în limba ecranului. */
  nume: (s: StoreyShape) => string;
  /** Aria, scrisă în limba ecranului. */
  arie: (m2: number) => string;
  /** Latura maximă a unei texturi pe placa video. */
  texturaMax: number;
  anizotropie: number;
}

export interface ApartamentPeMacheta extends ApartamentEtaj {
  /** Cota pardoselii lui. */
  z: number;
}

export interface InteriorEtaj {
  grup: THREE.Group;
  apartamente: ApartamentPeMacheta[];
  /** Plasele care opresc un clic (un perete interior în fața unei ferestre). */
  ocluzie: THREE.Mesh[];
  elibereaza(): void;
}

const MAT = {
  perete: new THREE.MeshStandardMaterial({ color: 0xf2efe9, roughness: 0.96 }),
  taietura: new THREE.MeshStandardMaterial({ color: 0xe1ddd5, roughness: 0.96 }),
  // treptele, abia mai gri decât zidul: scara se citește ca piesă, nu ca perete
  trepte: new THREE.MeshStandardMaterial({ color: 0xe6e2da, roughness: 0.9 }),
};
/** O casă a scării mai lată de atât ține două rampe și un podest (scara în U). */
const LATIME_SCARA_U_M = 2.2;

function prisma(inel: readonly number[][], z: number, h: number): THREE.BufferGeometry | null {
  if (inel.length < 3 || h <= 0.01) return null;
  const f = new THREE.Shape(inel.map(([x, y]) => new THREE.Vector2(x, y)));
  const g = new THREE.ExtrudeGeometry(f, { depth: h, bevelEnabled: false });
  g.translate(0, 0, z);
  return g;
}

/** O extrudare desfăcută pe grupurile ei — capacele (0) și laturile (1) —, ca
 *  apoi toate capacele etajului să fie o plasă și toate laturile alta. */
function peGrupuri(g: THREE.BufferGeometry): [THREE.BufferGeometry | null, THREE.BufferGeometry | null] {
  const pos = g.getAttribute("position"), nor = g.getAttribute("normal");
  const out: [THREE.BufferGeometry | null, THREE.BufferGeometry | null] = [null, null];
  for (const k of [0, 1] as const) {
    const gr = g.groups.filter((x) => (x.materialIndex ?? 0) === k);
    const n = gr.reduce((s, x) => s + Math.min(x.count, pos.count - x.start), 0);
    if (!n) continue;
    const P = new Float32Array(n * 3), N = new Float32Array(n * 3);
    let i = 0;
    for (const x of gr) {
      for (let v = x.start; v < Math.min(pos.count, x.start + x.count); v++, i++) {
        P[i * 3] = pos.getX(v); P[i * 3 + 1] = pos.getY(v); P[i * 3 + 2] = pos.getZ(v);
        N[i * 3] = nor.getX(v); N[i * 3 + 1] = nor.getY(v); N[i * 3 + 2] = nor.getZ(v);
      }
    }
    const r = new THREE.BufferGeometry();
    r.setAttribute("position", new THREE.BufferAttribute(P, 3));
    r.setAttribute("normal", new THREE.BufferAttribute(N, 3));
    out[k] = r;
  }
  g.dispose();
  return out;
}

/** Talpa unui gol în plan: dreptunghiul lui pe grosimea zidului, strâns cu
 *  `retras` pe grosime (ca zidul de fațadă). */
function talpaGolului(o: StoreyShape, retras: number): number[][] | null {
  if (o.cx == null || o.cy == null || !o.width_m) return null;
  const w = o.width_m, t = Math.max(0.05, (o.thickness_m ?? 0.3) - 2 * retras);
  const a = ((o.deg ?? 0) * Math.PI) / 180, c = Math.cos(a), s = Math.sin(a);
  return [[-w / 2, -t / 2], [w / 2, -t / 2], [w / 2, t / 2], [-w / 2, t / 2]]
    .map(([u, v]) => [o.cx! + u * c - v * s, o.cy! + u * s + v * c]);
}

type Inel = number[][];

/** NIMIC DIN INTERIOR NU IESE DIN PLANȘEU. Pereții scriși de compoziția
 *  fațadei (`origin: compozitie`) pot sta pe jumătate în afara zidăriei — un
 *  tronson de 30 cm între zid și 30 cm în fața lui (Demo-UTR6, Situația 3,
 *  14.10.2026) —, iar ridicați pe toată înălțimea etajului acopereau placajul
 *  ca un perete alb. Afară, adevărul e fațada machetei; deci ce iese din
 *  conturul planșeului (retras cu `RETRAS_FATADA_M`) se taie. Ce e înăuntru —
 *  aproape tot — trece de testul pe colțuri, fără tăiere. */
export function inPlanseu(inel: Inel, placi: readonly Inel[]): Inel[] {
  if (inel.every(([x, y]) => placi.some((r) => inPoligon(x, y, r as [number, number][]) || distContur(x, y, r as [number, number][]) < 0.001))) {
    return [inel];
  }
  const inchis = (r: Inel): [number, number][] => [...r, r[0]].map((q) => [q[0], q[1]] as [number, number]);
  return taie().intersection([inchis(inel)], placi.map((r) => [inchis(r)]))
    .map((poli) => poli[0].slice(0, -1) as Inel)
    .filter((r) => r.length >= 3);
}

/** Pereții unui plan, până la `sus`, ca liste de extrudări pe fel. */
function peretii(p: StoreyPlan, z: number, sus: number) {
  const part: THREE.BufferGeometry[] = [], zid: THREE.BufferGeometry[] = [];
  const h = sus - z;
  const placi = ((p.parts?.length ? p.parts : [p.outline]) as Inel[])
    .filter((r) => r.length >= 3).map((r) => insetInel(r, RETRAS_FATADA_M));
  const ridica = (inel: Inel, z0: number, hh: number, lista: THREE.BufferGeometry[]) => {
    for (const r of placi.length ? inPlanseu(inel, placi) : [inel]) {
      const g = prisma(r, z0, hh);
      if (g) lista.push(g);
    }
  };
  for (const w of p.walls) {
    if (w.kind === "parapet") continue;                 // aticul: e deasupra tăieturii
    const inel = w.kind === "facade" ? insetInel(w.poly, RETRAS_FATADA_M) : w.poly;
    ridica(inel, z, h, w.kind === "partition" ? part : zid);
  }
  // GOLURILE: zidul e întrerupt pe lățimea lor; sub fereastră, parapetul,
  // deasupra oricărui gol, buiandrugul până la tăietură
  for (const o of p.openings) {
    const talpa = talpaGolului(o, RETRAS_FATADA_M);
    if (!talpa) continue;
    const jos = o.tip === "Window" ? (o.sill_m ?? 0.9) : 0;
    const capul = jos + (o.height_m ?? (o.tip === "Window" ? 1.5 : 2.1));
    if (jos > 0.02) ridica(talpa, z, Math.min(jos, h), zid);
    if (capul < h - 0.02) ridica(talpa, z + capul, h - capul, zid);
  }
  return { part, zid };
}

/** SCARA NUCLEULUI în 3D, din cutia ei: dreaptă — o rampă pe lungul cutiei —
 *  sau în U, cu două rampe și podestul la capăt, când cutia e destul de lată.
 *  Fiecare treaptă e un bloc plin de la pardoseală, cât urcă ea, oprit la
 *  tăietură. Rampa urcă dinspre capătul de început al cadrului (`−x`). */
export function treptele(c: StoreyShape, z: number, hNivel: number, sus: number): THREE.BufferGeometry[] {
  const k = cadruCamerei(c.poly);
  if (!k || k.w < 1 || k.h < 0.6) return [];
  const u = k.h >= LATIME_SCARA_U_M;
  // în `scaraDin`, lățimea e pe cruce, adâncimea pe lungul rampelor
  const s = scaraDin(u ? "scaraU" : "scara", k.h, k.w, hNivel);
  const a = (k.deg * Math.PI) / 180, co = Math.cos(a), si = Math.sin(a);
  const out: THREE.BufferGeometry[] = [];
  /** O treaptă: de la `x0` la `x1` pe lungul cutiei, de la `y0` la `y1` pe cruce. */
  const treapta = (x0: number, x1: number, y0: number, y1: number, h: number) => {
    const hh = Math.min(h, sus - z);
    if (hh <= 0.01) return;
    const g = new THREE.BoxGeometry(Math.abs(x1 - x0), Math.abs(y1 - y0), hh);
    const mx = (x0 + x1) / 2, my = (y0 + y1) / 2;
    g.rotateZ(a);
    g.translate(k.cx + mx * co - my * si, k.cy + mx * si + my * co, z + hh / 2);
    out.push(g);
  };
  const x0 = -k.w / 2;
  if (!u) {
    const n = Math.max(1, s.rampe[0] - 1);
    for (let i = 0; i < n; i++) treapta(x0 + i * s.treapta, x0 + (i + 1) * s.treapta, -k.h / 2, k.h / 2, (i + 1) * s.pas);
    return out;
  }
  // ÎN U: prima rampă pe o jumătate, podestul la capăt, a doua înapoi pe cealaltă
  const [r1, r2] = s.rampe;
  const n1 = Math.max(1, r1 - 1);
  for (let i = 0; i < n1; i++) treapta(x0 + i * s.treapta, x0 + (i + 1) * s.treapta, -k.h / 2, -k.h / 2 + s.latRampa, (i + 1) * s.pas);
  const xp = x0 + s.lungRampa;
  treapta(xp, k.w / 2, -k.h / 2, k.h / 2, r1 * s.pas);
  const n2 = Math.max(1, r2 - 1);
  for (let i = 0; i < n2; i++) {
    treapta(xp - (i + 1) * s.treapta, xp - i * s.treapta, k.h / 2 - s.latRampa, k.h / 2, (r1 + i + 1) * s.pas);
  }
  return out;
}

/** Imaginea planului (SVG) coaptă într-o textură. */
async function coace(svg: string, W: number, H: number, o: OptiuniInterior): Promise<THREE.CanvasTexture> {
  const pxm = Math.min(PX_PE_M, o.texturaMax / Math.max(W, H));
  const w = Math.max(2, Math.round(W * pxm)), h = Math.max(2, Math.round(H * pxm));
  const img = new Image(w, h);
  img.src = "data:image/svg+xml;charset=utf-8,"
    + encodeURIComponent(svg.replace("<svg ", `<svg width="${w}" height="${h}" `));
  await img.decode();
  const cv = document.createElement("canvas");
  cv.width = w; cv.height = h;
  cv.getContext("2d")!.drawImage(img, 0, 0, w, h);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = o.anizotropie;
  return tex;
}

/** Mobilierul de planșă al unei camere, ca SVG — ținut minte pe fel și
 *  dimensiuni: apartamentele se repetă de la un etaj la altul. */
const mobilierCopt = new Map<string, string | null>();
function mobilierSvg(kind: string, w: number, h: number): string | null {
  const k = `${kind}|${w.toFixed(2)}|${h.toFixed(2)}`;
  let s = mobilierCopt.get(k);
  if (s === undefined) {
    // nucleul: liftul și ghena cu simbolul lor de planșă; scara e în 3D
    const piesa = kind === "lift" || kind === "ghena" ? piesaSingura(kind, w, h)
      : kind === "scara" ? null : piesaPlan(kind, w, h);
    // liniile planșei, mai blânde pe machetă: negrul de hârtie ar fi tăiat
    // pardoseala ca un desen lipit peste ea
    s = piesa ? faraVariabile(renderToStaticMarkup(piesa as React.ReactElement)).replace(/#111111/gi, "#5d6164") : null;
    mobilierCopt.set(k, s);
  }
  return s;
}

/** INTERIORUL ETAJULUI `nivel`, tăiat la `sus`, pentru toate corpurile lui. */
export async function construiesteInteriorul(planuri: readonly StoreyPlan[], fatade: readonly Fatada3[],
                                             nivel: number, sus: number, o: OptiuniInterior): Promise<InteriorEtaj> {
  const grup = new THREE.Group();
  const deArs: { dispose(): void }[] = [];
  const apartamente: ApartamentPeMacheta[] = [];
  const part: THREE.BufferGeometry[] = [], zid: THREE.BufferGeometry[] = [], trepte: THREE.BufferGeometry[] = [];
  const pardoseli: Promise<void>[] = [];
  for (const p of planuri) {
    if (p.level !== nivel) continue;
    const f = fatade.find((x) => x.storey === p.id);
    if (!f) continue;                                  // un plan fără volum pe machetă
    const z = f.cota;
    const ap = apartamenteleEtajului(p);
    apartamente.push(...ap.map((a) => ({ ...a, z })));
    const w = peretii(p, z, Math.min(sus, f.cota + f.inaltime));
    part.push(...w.part); zid.push(...w.zid);
    for (const c of p.cores) if (c.kind === "scara") trepte.push(...treptele(c, z, f.inaltime, sus));
    // INTRAREA DE LA TEREN (3.12): pardoseala holului coborât, treptele și
    // platforma lui — sub pardoseala etajului, deci sub orice tăietură
    const gropi = (f.denivelate ?? []).filter((g) => g.poly.length >= 3);
    for (const g of gropi) {
      const pl = prisma(insetInel(g.poly, 0.01), z + g.cota - 0.15, 0.15 + FINISAJ_M);
      if (pl) trepte.push(pl);
    }
    for (const tr of f.trepte ?? []) {
      const z0 = z + tr.cota0 + FINISAJ_M, h = tr.h - FINISAJ_M;
      trepte.push(...treptePeAmprenta(tr.poly, tr.deg, z0, h, tr.contratrepte));
      trepte.push(...mainiCurente(tr.poly, tr.deg, z0, h, tr.contratrepte));
    }
    for (const pl of f.platforme ?? []) {
      const p3 = platforma3d(pl.poly, z + pl.cota0 + FINISAJ_M);
      trepte.push(...p3.placa, ...p3.metal);
    }
    const plan = planulEtajului(p, ap, { cadru: cadruCamerei, mobilier: mobilierSvg, nume: o.nume, arie: o.arie });
    if (!plan) continue;
    pardoseli.push(coace(plan.svg, plan.W, plan.H, o).then((tex) => {
      // CU O LUMINĂ PROPRIE SLABĂ, din aceeași imagine: în umbra pereților
      // înalți pardoseala ieșea gri-albăstruie, iar nuanța tipului de
      // apartament se pierdea; umbrele rămân, doar că nu mai sting desenul
      const mat = new THREE.MeshStandardMaterial({ map: tex, roughness: 1, alphaTest: 0.5,
                                                   emissiveMap: tex, emissive: 0xffffff, emissiveIntensity: 0.38 });
      // CU GĂURILE HOLURILOR COBORÂTE (3.12): acolo pardoseala e mai jos, cu
      // placa ei; desenul rămâne pe același cadru, prin matricea texturii
      let geo: THREE.BufferGeometry;
      if (gropi.length) {
        const dreptunghi = new THREE.Shape([[plan.x0, plan.y0], [plan.x0 + plan.W, plan.y0],
                                            [plan.x0 + plan.W, plan.y0 + plan.H], [plan.x0, plan.y0 + plan.H]]
          .map(([x, y]) => new THREE.Vector2(x, y)));
        for (const g of gropi) dreptunghi.holes.push(new THREE.Path(g.poly.map(([x, y]) => new THREE.Vector2(x, y))));
        geo = new THREE.ShapeGeometry(dreptunghi);
        tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
        tex.repeat.set(1 / plan.W, 1 / plan.H);
        tex.offset.set(-plan.x0 / plan.W, -plan.y0 / plan.H);
      } else {
        geo = new THREE.PlaneGeometry(plan.W, plan.H);
      }
      const m = new THREE.Mesh(geo, mat);
      // 5 mm peste cota etajului: dedesubt e fața de sus a corpului etajului
      // de jos, iar placa balconului stă 5 mm SUB cotă (v. `RandareSit3D`);
      // la parter, peste straturile terenului (iarba, aleile, parcarea)
      if (gropi.length) m.position.set(0, 0, Math.max(z + 0.005, PESTE_TEREN_M));
      else m.position.set(plan.x0 + plan.W / 2, plan.y0 + plan.H / 2, Math.max(z + 0.005, PESTE_TEREN_M));
      m.receiveShadow = true;
      deArs.push(tex, mat, geo);
      grup.add(m);
    }));
  }
  const ocluzie: THREE.Mesh[] = [];
  for (const [lista, laturi, capace] of [[part, MAT.perete, MAT.taietura], [zid, MAT.perete, MAT.taietura]] as const) {
    const c: THREE.BufferGeometry[] = [], l: THREE.BufferGeometry[] = [];
    for (const g of lista) { const [a, b] = peGrupuri(g); if (a) c.push(a); if (b) l.push(b); }
    for (const [geos, mat] of [[c, capace], [l, laturi]] as const) {
      if (!geos.length) continue;
      const g = BGU.mergeGeometries(geos as THREE.BufferGeometry[], false);
      geos.forEach((x) => x.dispose());
      if (!g) continue;
      deArs.push(g);
      const m = new THREE.Mesh(g, mat);
      m.castShadow = true; m.receiveShadow = true;
      grup.add(m);
      ocluzie.push(m);
    }
  }
  if (trepte.length) {
    // toate scările etajului, o plasă
    const g = BGU.mergeGeometries(trepte.map((x) => x.toNonIndexed()), false);
    trepte.forEach((x) => x.dispose());
    if (g) {
      deArs.push(g);
      const m = new THREE.Mesh(g, MAT.trepte);
      m.castShadow = true; m.receiveShadow = true;
      grup.add(m);
      ocluzie.push(m);
    }
  }
  await Promise.all(pardoseli);
  return { grup, apartamente, ocluzie, elibereaza: () => deArs.forEach((d) => d.dispose()) };
}
